export type InteractionType = 'click' | 'tap' | 'key';

/**
 * Counts the visitor's clicks, taps and key presses (DEV-7892), as a bot
 * signal: people type an address with many key presses, a script often
 * fills it in at once. Reports the first interaction of the session once.
 *
 * Passive listeners on pointerdown and keydown only - never on pointer or
 * mouse moves, which fire dozens of times a second.
 */
export class InteractionTracker {

	private clicks = 0;
	private taps = 0;
	private keys = 0;
	private firstReported = false;
	private started = false;

	private readonly onPointerDown = ( event: Event ) => {

		const type: InteractionType = ( event as PointerEvent ).pointerType === 'mouse' ? 'click' : 'tap';
		type === 'click' ? this.clicks++ : this.taps++;
		this.reportFirst( type );
	};

	private readonly onKeyDown = () => {

		this.keys++;
		this.reportFirst( 'key' );
	};

	public constructor(
		private readonly target: EventTarget,
		private readonly onFirst: ( type: InteractionType, ms: number ) => void,
		private readonly now: () => number = () => performance.now(),
	) {}

	public start(): void {

		if( this.started ) {
			return;
		}

		this.started = true;
		this.target.addEventListener( 'pointerdown', this.onPointerDown, { passive: true } );
		this.target.addEventListener( 'keydown', this.onKeyDown, { passive: true } );
	}

	public stop(): void {

		this.started = false;
		this.target.removeEventListener( 'pointerdown', this.onPointerDown );
		this.target.removeEventListener( 'keydown', this.onKeyDown );
	}

	/** The counts since the last takeStepCounts(), left as they are (DEV-7892 review). */
	public peekStepCounts(): { clicks: number, taps: number, keys: number } {

		return { clicks: this.clicks, taps: this.taps, keys: this.keys };
	}

	/** The counts since the last call, which start again at 0. */
	public takeStepCounts(): { clicks: number, taps: number, keys: number } {

		const counts = this.peekStepCounts();
		this.clicks = this.taps = this.keys = 0;
		return counts;
	}

	private reportFirst( type: InteractionType ): void {

		if( this.firstReported ) {
			return;
		}

		this.firstReported = true;

		try {
			this.onFirst( type, this.now() );
		} catch {
			// Never surface telemetry errors to the form.
		}
	}
}
